/**
 * Place value as a physical thing: ones you can count, ten of which snap into
 * a rod, ten rods into a flat, ten flats into a cube.
 *
 * The whole idea a child is meant to arrive at is that grouping and breaking
 * apart never change how much you have — so that is exactly what the tests
 * assert about these functions, not the shape of the return value.
 */

/** Counts indexed by power of ten: index 0 is ones, index 3 is thousands. */
export type Counts = number[];

export const PLACE_NAMES = [
  'ones',
  'tens',
  'hundreds',
  'thousands',
  'ten thousands',
  'hundred thousands',
  'millions',
];

export const TOP_PLACE = PLACE_NAMES.length - 1;

/** What one piece at this place is worth, as a word a child can say. */
export const PIECE_NAMES = ['one', 'ten', 'hundred', 'thousand', 'ten thousand', 'hundred thousand', 'million'];

export function countsOf(n: number): Counts {
  const out: Counts = new Array(TOP_PLACE + 1).fill(0);
  let rest = Math.max(0, Math.floor(n));
  for (let place = 0; place <= TOP_PLACE && rest > 0; place++) {
    if (place === TOP_PLACE) {
      out[place] = rest;
      break;
    }
    out[place] = rest % 10;
    rest = Math.floor(rest / 10);
  }
  return out;
}

export function valueOf(counts: Counts): number {
  let total = 0;
  for (let place = 0; place < counts.length; place++) total += counts[place] * 10 ** place;
  return total;
}

export function canGroup(counts: Counts, place: number): boolean {
  return place < TOP_PLACE && counts[place] >= 10;
}

/** Ten pieces here become one piece next door. The value does not move. */
export function group(counts: Counts, place: number): Counts {
  if (!canGroup(counts, place)) return counts;
  const out = [...counts];
  out[place] -= 10;
  out[place + 1] += 1;
  return out;
}

export function canBreak(counts: Counts, place: number): boolean {
  return place > 0 && counts[place] >= 1;
}

/** One piece here becomes ten next door down. Also value-preserving. */
export function breakOne(counts: Counts, place: number): Counts {
  if (!canBreak(counts, place)) return counts;
  const out = [...counts];
  out[place] -= 1;
  out[place - 1] += 10;
  return out;
}

/** Carry everything that can be carried — the tidy form of the same number. */
export function tidy(counts: Counts): Counts {
  let out = [...counts];
  for (let place = 0; place < TOP_PLACE; place++) {
    while (canGroup(out, place)) out = group(out, place);
  }
  return out;
}

export function isTidy(counts: Counts): boolean {
  for (let place = 0; place < TOP_PLACE; place++) {
    if (counts[place] >= 10) return false;
  }
  return true;
}

export function add(counts: Counts, place: number, howMany = 1): Counts {
  const out = [...counts];
  out[place] += howMany;
  return out;
}

/** Take one piece away, if there is one to take. */
export function removeOne(counts: Counts, place: number): Counts {
  if (counts[place] <= 0) return counts;
  const out = [...counts];
  out[place] -= 1;
  return out;
}
